import {
	AOFL_PRODUCT_UUIDS,
	CURRENCY_CODE,
	LINK_FROM_APP_PLATFORMS,
	REG_PATH_KEYS,
	SUBSCRIPTION_TYPES,
	THIRD_PARTY_EVENTS,
} from '$lib/utils/constants';
import { isEntryFromAltPaymentAppLink } from '$lib/utils/link-from-app-platform';
import { compare } from 'compare-versions';
import { Sentry } from '$lib/services/sentry';
import { ProductExperienceService } from '$lib/services/product-experience';

/************************************************
 *************** CONSTANTS **********************
 *************************************************/
/**
 * @typedef {'appLoad' | 'auxiliary' | 'click' | 'login' | 'ltv' | 'optOut' | 'pageLoad' | 'pageLoadNmhp' | 'pageLoadSub' | 'purchase' | 'reactivate' | 'subscribe' | 'upgrade'} EventType
 */
export const EventType = Object.freeze({
	AppLoad: 'appLoad',
	Auxiliary: 'auxiliary',
	Click: 'click',
	LogIn: 'login',
	Ltv: 'ltv',
	OptOut: 'optOut',
	PageLoad: 'pageLoad',
	PageLoadNmhp: 'pageLoadNmhp',
	PageLoadSub: 'pageLoadSub',
	Purchase: 'purchase',
	Reactivate: 'reactivate',
	Subscribe: 'subscribe',
	Upgrade: 'upgrade',
});

export const LOG_EVENT_TOKENS = {
	NMHP_LOAD: 'yayo86',
	NMHP_LOAD_AMAZON: 'ph9b9z', // this event is specific for firebase + Amazon
	SUB_PAGE_LOAD: '87ncx9',
	SUB_PAGE_LOAD_AMAZON: '6hvoqq',
	DEFAULT_PURCHASE_REVENUE_EVENT: 'wiyrko',
	AMAZON_SUBSCRIPTION: 'rvsr1g',
	AMAZON_REACTIVATION: 'gi600j',
	DEFAULT_PROSPECT_REGISTER: '503p8k',
	SUBSCRIPTION: 'byuafh',
	REACTIVATION: 'xqmv16',
	// Server-side Adjust event tokens
	ADJUST_2_0_PURCHASE_REVENUE_EVENT: 'szluwh',
	ADJUST_2_0_SUBSCRIPTION: 'z0s8tt',
	// Decomission list
	DEFAULT_LOGIN: '1kjd9f',
	AMAZON_LOGIN: 'gwbkpa',
};

/**
 * A mapping of page paths to their respective event tokens.
 * @type {{ [key:string]: { AMAZON: string; DEFAULT: string; pageLoadEvent: 'pageLoadNmhp' | 'pageLoadSub' }}} PAGE_MAP
 * @todo set this up so that it can be agnostic of Firebase or Adjust (or really any transport?)
 */
const PAGE_MAP = {
	'/': {
		AMAZON: LOG_EVENT_TOKENS.NMHP_LOAD_AMAZON,
		DEFAULT: LOG_EVENT_TOKENS.NMHP_LOAD,
		pageLoadEvent: EventType.PageLoadNmhp,
	},
	'/home': {
		AMAZON: LOG_EVENT_TOKENS.NMHP_LOAD_AMAZON,
		DEFAULT: LOG_EVENT_TOKENS.NMHP_LOAD,
		pageLoadEvent: EventType.PageLoadNmhp,
	},
	'/subscription': {
		AMAZON: LOG_EVENT_TOKENS.SUB_PAGE_LOAD_AMAZON,
		DEFAULT: LOG_EVENT_TOKENS.SUB_PAGE_LOAD,
		pageLoadEvent: EventType.PageLoadSub,
	},
};

const SUB_EVENT_MAP = {
	[SUBSCRIPTION_TYPES.NEW_SUBSCRIPTION]: {
		AMAZON: LOG_EVENT_TOKENS.AMAZON_SUBSCRIPTION,
		DEFAULT: LOG_EVENT_TOKENS.SUBSCRIPTION,
	},
	[SUBSCRIPTION_TYPES.EXISTING_SUBSCRIPTION]: {
		AMAZON: LOG_EVENT_TOKENS.AMAZON_REACTIVATION,
		DEFAULT: LOG_EVENT_TOKENS.AMAZON_SUBSCRIPTION,
	},
};

const LOGIN_EVENT_MAP = (/** @type {boolean} */ firstTimeLogin) => {
	// TODO: need new tokens for first time login
	if (firstTimeLogin)
		return {
			AMAZON: LOG_EVENT_TOKENS.AMAZON_LOGIN,
			DEFAULT: LOG_EVENT_TOKENS.DEFAULT_LOGIN,
		};
	else
		return {
			AMAZON: LOG_EVENT_TOKENS.AMAZON_LOGIN,
			DEFAULT: LOG_EVENT_TOKENS.DEFAULT_LOGIN,
		};
};

export const LOG_EVENT_TYPES = {
	ADJUST: 'adjust',
	FIREBASE: 'firebase',
};

export const appCallCallbacks = {
	ADJUST_EVENT: 'adjustCallback',
	FIREBASE_EVENT: 'firebaseCallback',
};

/**
 * A mapping of AOFL product UUIDs to their respective event tokens considering platform context.
 * Not all of these events have corresponding EventType entries, and not all of them
 * are posted in the context of this client codebase.  Those that are not are not included here for reference.
 * These event tokens are specific to Adjust.
 * @type {{ [key:string]: { AMAZON: { appToken: string; eventTokens: { [key:string]: string; } }; DEFAULT: { appToken: string; eventTokens: { [key:string]: string; } }; }}} ADJUST_TOKENS
 * @todo Do this same thing for Firebase transport methods??
 */
export const ADJUST_TOKENS = {
	[AOFL_PRODUCT_UUIDS.ABCMOUSE]: {
		AMAZON: {
			appToken: '86kae45v8v7k',
			eventTokens: {
				[EventType.LogIn]: 'gwbkpa',
				[EventType.OptOut]: 'm0xllb',
				[EventType.PageLoadNmhp]: 'ph9b9z',
				[EventType.PageLoadSub]: '6hvoqq',
				[EventType.Reactivate]: 'gi600j',
				[EventType.Subscribe]: 'rvsr1g',
			},
		},
		DEFAULT: {
			appToken: 'kn43j6ao4j5s',
			eventTokens: {
				[EventType.LogIn]: 'lkjd9f',
				loginFirstTime: '8e7ovf',
				[EventType.Ltv]: 'wiyrko',
				[EventType.OptOut]: '7kvawc',
				[EventType.PageLoadNmhp]: 'yayo86',
				[EventType.PageLoadSub]: '87ncx9',
				[EventType.Reactivate]: 'xqmv16',
				[EventType.Subscribe]: 'byuafh',
			},
		},
	},
	[AOFL_PRODUCT_UUIDS.ABCMOUSE_2_0]: {
		AMAZON: {
			appToken: 'dgmeibjohdds',
			eventTokens: {
				[EventType.LogIn]: 'vnldck',
				[EventType.Ltv]: 'elvjyu',
				[EventType.OptOut]: 'jd91zq',
				[EventType.PageLoadNmhp]: 'adrjbi',
				[EventType.PageLoadSub]: 'pvns19',
				[EventType.Purchase]: 'indk5q',
				[EventType.Subscribe]: 'bnafy8',
			},
		},
		DEFAULT: {
			appToken: 'qh1j5srux6o0',
			eventTokens: {
				[EventType.LogIn]: 'dpibvd',
				[EventType.Ltv]: 'wxr4ax',
				[EventType.OptOut]: 'bx1gd8',
				[EventType.PageLoadNmhp]: 'qded35',
				[EventType.PageLoadSub]: 'z0s8tt',
				[EventType.Purchase]: 'crilbd',
				[EventType.Subscribe]: 'szluwh',
			},
		},
	},
};

/**
 * Registration paths where the user may subscribe to ABCmouse 2.0 in-product but attribution
 * should post to the ABCmouse 1.0 Adjust app (`AOFL_PRODUCT_UUIDS.ABCMOUSE`).
 * Includes iOS and Android alt payment 1.0 subscriber/resubscriber paths.
 */
const ADJUST_ABCMOUSE_1_0_ATTRIBUTION_REG_PATH_KEYS = new Set([
	REG_PATH_KEYS.IOS_ALT_PAYMENT_SUBSCRIBER_1_0,
	REG_PATH_KEYS.IOS_ALT_PAYMENT_RESUBSCRIBER_1_0,
	REG_PATH_KEYS.ANDROID_ALT_PAYMENT_SUBSCRIBER_1_0,
	REG_PATH_KEYS.ANDROID_ALT_PAYMENT_RESUBSCRIBER_1_0,
]);

/**
 * @param {string | undefined} regPathKey - `userMetaData.subscription_path` when present.
 * @returns {boolean}
 */
function shouldUseAbcmouse1_0AdjustAppForRegPath(regPathKey) {
	return (
		typeof regPathKey === 'string' && ADJUST_ABCMOUSE_1_0_ATTRIBUTION_REG_PATH_KEYS.has(regPathKey)
	);
}

/**
 * Use ABCmouse 1.0 Adjust app when reg path is iOS/Android alt 1.0, or session came from a 1.0 app link (inbound / query).
 * @param {string | undefined} regPathKey - `userMetaData.subscription_path` when present.
 * @param {string | null | undefined} linkFromAppPlatform - `meta.linkFromAppPlatform` (e.g. 1.0 app link on page load).
 * @returns {boolean}
 */
function shouldUseAbcmouse1_0AdjustApp(regPathKey, linkFromAppPlatform) {
	return (
		shouldUseAbcmouse1_0AdjustAppForRegPath(regPathKey) ||
		linkFromAppPlatform === LINK_FROM_APP_PLATFORMS.IOS_1_0 ||
		linkFromAppPlatform === LINK_FROM_APP_PLATFORMS.ANDROID_1_0
	);
}

/**
 * Assesses an event context to get the appropriate Adjust event token and app token
 * for posting a server-to-server event to Adjust.
 * @param {object} params - The parameters for the function.
 * @param {string} [params.aoflProductUuid] - A value in the AOFL_PRODUCT_UUIDS enum
 * @param {EventType} params.eventType - The type of event.
 * @param {'AMAZON'|'DEFAULT'} params.platform - The app platform of the event.
 * @param {string} [params.regPathKey] - From `event.detail.regPathKey` on subscribe / upgrade / auxiliary (not used for page load).
 * @param {string | null | undefined} [params.linkFromAppPlatform] - From `meta.linkFromAppPlatform` (e.g. 1.0 app link on page load).
 * @returns {{eventToken: string|null, appToken: string|null}} - The event token and app token for the event.
 */
function getAdjustEventConfig({
	aoflProductUuid,
	eventType,
	platform,
	regPathKey,
	linkFromAppPlatform,
}) {
	let resolvedAoflProductUuid =
		aoflProductUuid === undefined ? ProductExperienceService.AOFL_PRODUCT_UUID : aoflProductUuid;
	if (shouldUseAbcmouse1_0AdjustApp(regPathKey, linkFromAppPlatform)) {
		resolvedAoflProductUuid = AOFL_PRODUCT_UUIDS.ABCMOUSE;
	}
	const eventToken =
		ADJUST_TOKENS[resolvedAoflProductUuid]?.[platform]?.eventTokens?.[eventType] ?? null;
	const appToken = ADJUST_TOKENS[resolvedAoflProductUuid]?.[platform]?.appToken ?? null;
	if (!(eventToken && appToken)) {
		Sentry.captureMessage('Adjust event config unresolved', {
			level: 'warning',
			extra: {
				action: 'adjust_event_config_unresolved',
				resolvedAoflProductUuid,
				platform,
				eventType,
				...(regPathKey != null && regPathKey !== '' ? { regPathKey } : {}),
				...(linkFromAppPlatform != null && linkFromAppPlatform !== ''
					? { linkFromAppPlatform }
					: {}),
			},
		});
	}
	return { eventToken, appToken };
}

/************************************************
 *************** TRANSFORMERS *******************
 *************************************************/

/**
 * Device identifiers for server-side Adjust event_info (alt-payment / in-app flows).
 * idfa is intentionally excluded from this flow.
 * @param {import('./types').Events.SubscribeEvent['detail'] | import('./types').Events.LoadEvent['detail'] | import('./types').Events.LogInEvent['detail'] | undefined} detail - Subscribe or page-load event detail with Adjust device IDs (adid, udid, idfv, gps_adid, android_id, app_instance_id).
 * @returns {{ adid: string; android_id: string; app_instance_id: string; gps_adid: string; idfa: string; idfv: string; udid: string; }}
 */
function ssAdjustDeviceIdsFromDetail(detail) {
	return {
		adid: detail?.adid || '',
		android_id: detail?.android_id || '',
		app_instance_id: detail?.app_instance_id || '',
		gps_adid: detail?.gps_adid || '',
		idfa: detail?.idfa || '',
		idfv: detail?.idfv || '',
		udid: detail?.udid || '',
	};
}

/**
 * Transformer that filters out events that are not emitted in app.
 * @param {Array<import('./types').Events.Event<EventType, object>>} events - Events to filter.
 * @returns {Array<import('./types').Events.Event<EventType, object>>}
 */
export function filterInAppEvent(events) {
	const eventArray = Array.isArray(events) ? events : [events];
	return eventArray.filter((event) => event?.meta?.inApp !== false);
}

/**
 * @param {Array<import('./types').Events.Event<EventType, object>>} events incoming event
 * @returns {Array<object>}
 */
export function assignAdjust(events) {
	const eventArray = Array.isArray(events) ? events : [events];
	return eventArray.flatMap((eachEvent) => {
		const { meta, ...restOfEvent } = eachEvent;
		// TODO: add assertions instead of ?
		const appVersion = String(meta?.appVersion ?? '8.23.1');
		const isLegacyEvent = compare(appVersion, '8.23.0', '<=');
		const event = {
			...restOfEvent,
			request: appCallCallbacks.ADJUST_EVENT,
		};
		if (isLegacyEvent) return event;
		return {
			...event,
			type: LOG_EVENT_TYPES.ADJUST,
		};
	});
}

/**
 * @param {Array<import('./types').Events.Event<EventType, object>>} event incoming event
 * @returns {Array<object>}
 */
export function assignFirebase(event) {
	const eventArray = Array.isArray(event) ? event : [event];
	return eventArray.flatMap((eachEvent) => {
		// TODO: add assertions instead of ?
		const { meta, ...restOfEvent } = eachEvent;
		const appVersion = String(meta?.appVersion ?? '8.23.1');
		const isLegacyEvent = compare(appVersion, '8.23.0', '<=');
		const event = {
			...restOfEvent,
			request: appCallCallbacks.FIREBASE_EVENT,
		};
		if (isLegacyEvent) return event;
		return {
			...event,
			type: LOG_EVENT_TYPES.FIREBASE,
		};
	});
}

/**
 * This function is specific to the Adjust transport that posts
 * to adjust via app calls.
 * @param {Array<object>} event incoming event
 * @param {import('./types').Events.SubscribeEvent} initialEvent incoming event
 * @returns {Array<object>}
 * @todo dynamically resolve the event token based on the context and transport caller (this is set up for Adjust but not Firebase)
 */
export function processSubscribeEvent(event, initialEvent) {
	const subType = initialEvent.detail.subscriptionType;
	const eventType = initialEvent.meta.isAmazonApp
		? SUB_EVENT_MAP[subType].AMAZON
		: SUB_EVENT_MAP[subType].DEFAULT;
	const eventArray = Array.isArray(event) ? event : [event];
	return eventArray.flatMap((_eachEvent) => {
		return [
			{
				meta: initialEvent.meta,
				event: eventType,
				customParameters: JSON.stringify({
					...ssAdjustDeviceIdsFromDetail(initialEvent.detail),
					device_id: initialEvent.deviceId,
					family_ID: initialEvent.detail.userToProductId,
					legacy_track_cookie: initialEvent.legacyTrackCookie || 'none',
				}),
			},
		];
	});
}

/**
 * @param {Array<import('./types').Events.SubscribeEvent>} event incoming event
 * @param {import('./types').Events.SubscribeEvent} initialEvent incoming event
 * @returns {Array<object>}
 * @todo dynamically resolve the event token based on the context and transport caller (this is set up for Adjust but not Firebase)
 */
export function appendRevenueEvent(event, initialEvent) {
	if (initialEvent.detail.subscriptionType === SUBSCRIPTION_TYPES.EXISTING_SUBSCRIPTION)
		return event;
	const eventArray = Array.isArray(event) ? event : [event];
	return eventArray.flatMap((eachEvent) => {
		return [
			...event,
			{
				meta: eachEvent.meta,
				event: LOG_EVENT_TOKENS.DEFAULT_PURCHASE_REVENUE_EVENT,
				customParameters: JSON.stringify({
					...ssAdjustDeviceIdsFromDetail(initialEvent.detail),
					device_id: initialEvent.deviceId,
					family_ID: initialEvent.detail.userToProductId,
					legacy_track_cookie: initialEvent.legacyTrackCookie || 'none',
					product_hash: initialEvent.detail.productHash,
				}),
				feature: JSON.stringify({
					'event.setRevenue': {
						value: initialEvent.detail.ltv
							? initialEvent.detail.ltv
							: initialEvent.detail.productPrice,
						currency: initialEvent.detail.productCurrency,
					},
				}),
			},
		];
	});
}

/**
 * @param {Array<import('./types').Events.SubscribeEvent>} event incoming event
 * @param {import('./types').Events.SubscribeEvent} _initialEvent incoming event
 * @returns {Array<object>}
 * @todo dynamically resolve the event token based on the context and transport caller (this is set up for Adjust but not Firebase)
 */
export function processRevenueEvent(event, _initialEvent) {
	const eventArray = Array.isArray(event) ? event : [event];
	return eventArray.flatMap((eachEvent) => {
		return [
			{
				meta: eachEvent.meta,
				event: LOG_EVENT_TOKENS.DEFAULT_PURCHASE_REVENUE_EVENT,
				customParameters: JSON.stringify({
					...ssAdjustDeviceIdsFromDetail(eachEvent.detail),
					device_id: eachEvent.deviceId,
					family_ID: eachEvent.detail.userToProductId,
					legacy_track_cookie: eachEvent.legacyTrackCookie,
					product_hash: eachEvent.detail.productHash,
				}),
				feature: JSON.stringify({
					'event.setRevenue': {
						value: eachEvent.detail.productPrice,
						currency: eachEvent.detail.productCurrency,
					},
				}),
			},
		];
	});
}

/**
 * @param {Array<import('./types').Events.LoadEvent>} event incoming event
 * @param {Array<import('./types').Events.LoadEvent>} _initialValue incoming event
 * @returns {Array<object>}
 * @todo dynamically resolve the event token based on the context and transport caller (this is set up for Adjust but not Firebase)
 */
export function filterAndTransportForCorePages(event, _initialValue) {
	const eventArray = Array.isArray(event) ? event : [event];
	const PAGES_TRACKED_BY_EXTERNAL_SRC = Object.keys(PAGE_MAP);
	return eventArray
		.filter((eachEvent) => PAGES_TRACKED_BY_EXTERNAL_SRC.includes(eachEvent.detail.pageName))
		.map((eachPage) => ({
			meta: eachPage.meta,
			event: eachPage.meta.isAmazonApp
				? PAGE_MAP[eachPage.detail.pageName].AMAZON
				: PAGE_MAP[eachPage.detail.pageName].DEFAULT,
			customParameters: JSON.stringify({
				...ssAdjustDeviceIdsFromDetail(eachPage.detail),
				device_id: eachPage.deviceId,
				family_ID: 0,
				legacy_track_cookie: eachPage.legacyTrackCookie || 'none',
			}),
		}));
}

/**
 * @param {Array<import('./types').Events.LogInEvent>} event incoming event
 * @param {Array<import('./types').Events.LogInEvent>} _initialEvent of incoming event
 * @returns {Array<object>}
 * @todo dynamically resolve the event token based on the context and transport caller (this is set up for Adjust but not Firebase)
 */
export function processLoginEvent(event, _initialEvent) {
	const eventArray = Array.isArray(event) ? event : [event];
	return eventArray.map((eachEvent) => ({
		meta: eachEvent.meta,
		event: eachEvent.meta.isAmazonApp
			? LOGIN_EVENT_MAP(eachEvent.detail.firstTimeLogin).AMAZON
			: LOGIN_EVENT_MAP(eachEvent.detail.firstTimeLogin).DEFAULT,
		customParameters: JSON.stringify({
			...ssAdjustDeviceIdsFromDetail(eachEvent.detail),
			device_id: eachEvent.deviceId,
			family_ID: eachEvent.detail.userToProductId,
			legacy_track_cookie: eachEvent.legacyTrackCookie || 'none',
		}),
	}));
}

/**
 * Processes subscription events (subscribe) for server-side Adjust tracking (iOS/Android alt-payment).
 * Only generates events if subscription type is new subscription (or 2.0 existing-sub bypass), otherwise returns empty array.
 * Returns only the SUBSCRIBE event with full API payload structure.
 * This event is for counting purposes only and should not post any revenue values.
 * Note: conversion_event_id needs to be empty, since it is used by the third party conversion queue
 * processor to dedupe events for other non-Adjust partners, and the field has no relevance for Adjust.
 * @param {import('./types').Events.SubscribeEvent} _event incoming event
 * @param {import('./types').Events.SubscribeEvent} initialEvent initial event with full context
 * @returns {Array<object>}
 */
export function processSSAdjustSubscriptionEvent(_event, initialEvent) {
	const countryCode = initialEvent.meta?.viewerCountry || 'US';
	const eventConfig = getAdjustEventConfig({
		aoflProductUuid: initialEvent?.aoflProductId, // in case product subscription is different from product experience
		eventType: EventType.Subscribe,
		linkFromAppPlatform: initialEvent.meta?.linkFromAppPlatform,
		platform: initialEvent.meta.isAmazonApp ? 'AMAZON' : 'DEFAULT',
		regPathKey: initialEvent.detail?.regPathKey,
	});

	const transformedEvents = [];
	if (
		eventConfig?.eventToken &&
		(initialEvent.detail?.subscriptionType === SUBSCRIPTION_TYPES.NEW_SUBSCRIPTION ||
			allowExistingSubscriptions(initialEvent))
	) {
		transformedEvents.push({
			conversion_event_id: '',
			country_code: countryCode,
			hashed_email: '',
			user_agent: '',
			event_info: {
				...ssAdjustDeviceIdsFromDetail(initialEvent.detail),
				app_token: initialEvent.detail?.app_token || eventConfig.appToken,
				device_id: initialEvent.deviceId || '',
				event: eventConfig.eventToken,
				legacy_track_cookie: initialEvent.legacyTrackCookie || '',
				product_hash: initialEvent.detail?.productHash || '',
			},
			event_source: [LOG_EVENT_TYPES.ADJUST],
			event_type: THIRD_PARTY_EVENTS.SUBSCRIBE,
			event_source_url: '',
			user_to_product_uuid: initialEvent.detail?.userToProductId || '',
		});
	}

	return transformedEvents;
}

/**
 * Processes purchase events (subscribe/upgrade/auxiliary/reactivate) for server-side Adjust tracking.
 * Only generates events if initial price is greater than 0, otherwise returns empty array.
 * Returns only the PURCHASE event with full API payload structure.
 * Note: conversion_event_id needs to be empty, since it is used by the third party conversion queue
 * processor to dedupe events for other non-Adjust partners, and the field has no relevance for Adjust.
 * @param {import('./types').Events.SubscribeEvent | import('./types').Events.UpgradeEvent} _event incoming event
 * @param {import('./types').Events.SubscribeEvent | import('./types').Events.UpgradeEvent} initialEvent initial event with full context
 * @returns {Array<object>}
 */
export function processSSAdjustPurchaseEvent(_event, initialEvent) {
	const countryCode = initialEvent.meta?.viewerCountry || 'US';
	const eventConfig = getAdjustEventConfig({
		aoflProductUuid: initialEvent?.aoflProductId, // in case product subscription is different from product experience
		eventType: EventType.Purchase,
		linkFromAppPlatform: initialEvent.meta?.linkFromAppPlatform,
		platform: initialEvent.meta.isAmazonApp ? 'AMAZON' : 'DEFAULT',
		regPathKey: initialEvent.detail?.regPathKey,
	});
	const productPurchasePrice = initialEvent.detail?.productPurchasePrice ?? '0.00';
	const transformedEvents = [];
	if (eventConfig?.eventToken && parseFloat(productPurchasePrice) > 0) {
		transformedEvents.push({
			conversion_event_id: '',
			country_code: countryCode,
			hashed_email: '',
			user_agent: '',
			event_info: {
				...ssAdjustDeviceIdsFromDetail(initialEvent.detail),
				app_token: initialEvent.detail?.app_token || eventConfig.appToken,
				currency: initialEvent.detail?.productCurrency || CURRENCY_CODE.US_DOLLAR,
				device_id: initialEvent.deviceId || '',
				event: eventConfig.eventToken,
				legacy_track_cookie: initialEvent.legacyTrackCookie || '',
				price: productPurchasePrice,
				product_hash: initialEvent.detail?.productHash || '',
			},
			event_source: [LOG_EVENT_TYPES.ADJUST],
			event_type: THIRD_PARTY_EVENTS.SUBSCRIBE,
			event_source_url: '',
			user_to_product_uuid: initialEvent.detail?.userToProductId || '',
		});
	}

	return transformedEvents;
}

/**
 * Processes LTV events (subscribe) for server-side Adjust tracking (iOS/Android alt-payment).
 * Only generates events if subscription type is new subscription (or 2.0 existing-sub bypass), otherwise returns empty array.
 * Returns only the LTV event with full API payload structure.
 * Note: conversion_event_id needs to be empty, since it is used by the third party conversion queue
 * processor to dedupe events for other non-Adjust partners, and the field has no relevance for Adjust.
 * @param {import('./types').Events.SubscribeEvent} _event incoming event
 * @param {import('./types').Events.SubscribeEvent} initialEvent initial event with full context
 * @returns {Array<object>}
 */
export function processSSAdjustLtvEvent(_event, initialEvent) {
	const countryCode = initialEvent.meta?.viewerCountry || 'US';
	const eventConfig = getAdjustEventConfig({
		aoflProductUuid: initialEvent?.aoflProductId, // in case product subscription is different from product experience
		eventType: EventType.Ltv,
		linkFromAppPlatform: initialEvent.meta?.linkFromAppPlatform,
		platform: initialEvent.meta.isAmazonApp ? 'AMAZON' : 'DEFAULT',
		regPathKey: initialEvent.detail?.regPathKey,
	});

	const transformedEvents = [];
	if (
		eventConfig?.eventToken &&
		(initialEvent.detail?.subscriptionType === SUBSCRIPTION_TYPES.NEW_SUBSCRIPTION ||
			allowExistingSubscriptions(initialEvent))
	) {
		transformedEvents.push({
			conversion_event_id: '',
			country_code: countryCode,
			hashed_email: '',
			user_agent: '',
			event_info: {
				...ssAdjustDeviceIdsFromDetail(initialEvent.detail),
				app_token: initialEvent.detail?.app_token || eventConfig.appToken,
				currency: CURRENCY_CODE.US_DOLLAR, // LTV always in USD
				device_id: initialEvent.deviceId || '',
				event: eventConfig.eventToken,
				legacy_track_cookie: initialEvent.legacyTrackCookie || '',
				ltv: initialEvent?.detail?.ltv || '',
				product_hash: initialEvent.detail?.productHash || '',
			},
			event_source: [LOG_EVENT_TYPES.ADJUST],
			event_type: THIRD_PARTY_EVENTS.SUBSCRIBE,
			event_source_url: '',
			user_to_product_uuid: initialEvent.detail?.userToProductId || '',
		});
	}

	return transformedEvents;
}

/**
 * Processes subscription events for server-to-server Adjust tracking.
 * @param {import('./types').Events.SubscribeEvent} event incoming event
 * @param {import('./types').Events.SubscribeEvent} initialEvent initial event with full context
 * @returns {Array<object>}
 */
export function processSSAdjustSubscribeEvent(event, initialEvent) {
	const ltvEvents = processSSAdjustLtvEvent(event, initialEvent);
	const purchaseEvents = processSSAdjustPurchaseEvent(event, initialEvent);
	const subscriptionEvents = processSSAdjustSubscriptionEvent(event, initialEvent);
	return [...ltvEvents, ...purchaseEvents, ...subscriptionEvents];
}

/**
 * Processes upgrade events for server-side Adjust tracking.
 * @param {import('./types').Events.UpgradeEvent} event incoming event
 * @param {import('./types').Events.UpgradeEvent} initialEvent initial event with full context
 * @returns {Array<object>}
 */
export function processSSAdjustUpgradeEvent(event, initialEvent) {
	return processSSAdjustPurchaseEvent(event, initialEvent);
}

/**
 * Filters events to only those that originated from an iOS or Android alt payment app link.
 * Used as a transformer gate for server-side Adjust tracking on both platforms.
 *
 * Gate uses meta.linkFromAppPlatform (set via inbound-gateway or app init), not meta.inApp.
 * Typical alt-payment opens system browser (inApp false); in-app webview flows must still set
 * linkFromAppPlatform and device identifiers (query params or inbound-gateway).
 * @param {Array<import('./types').Events.Event<EventType, object>>} events - Events to filter.
 * @returns {Array<import('./types').Events.Event<EventType, object>>}
 */
export function onlyAllowAltPaymentEvent(events) {
	const eventArray = Array.isArray(events) ? events : [events];
	return eventArray.filter((eachEvent) =>
		isEntryFromAltPaymentAppLink(eachEvent.meta?.linkFromAppPlatform)
	);
}

/** @deprecated Use {@link onlyAllowAltPaymentEvent} instead. */
export const onlyAllowIOSAltEvent = onlyAllowAltPaymentEvent;

/**
 * If an event transformer is filtering out existing subscriptions, this function
 * determines whether to bypass that restriction.
 * @param {import('./types').Events.Event<EventType, object>} initialEvent - initial event with full context
 * @returns {boolean} - true if the initial event is an existing subscription, false otherwise
 */
function allowExistingSubscriptions(initialEvent) {
	return initialEvent?.aoflProductId === AOFL_PRODUCT_UUIDS.ABCMOUSE_2_0;
}

/**
 * Processes page load events for server-side Adjust tracking (alt-payment).
 * Generates INITIATE_CHECKOUT when a page-load Adjust token exists for the page.
 * @param {import('./types').Events.LoadEvent} _event incoming event (intentionally not used)
 * @param {import('./types').Events.LoadEvent} initialEvent initial event with full context
 * @returns {Array<object>}
 */
export function processSSAdjustPageLoadEvent(_event, initialEvent) {
	const pageName = initialEvent.detail?.pageName || '';
	const countryCode = initialEvent.meta?.viewerCountry || 'US';
	const eventConfig = getAdjustEventConfig({
		eventType: PAGE_MAP[pageName]?.pageLoadEvent ?? EventType.PageLoadSub,
		linkFromAppPlatform: initialEvent.meta?.linkFromAppPlatform,
		platform: initialEvent.meta.isAmazonApp ? 'AMAZON' : 'DEFAULT',
	});

	const transformedEvents = [];
	if (eventConfig?.eventToken) {
		transformedEvents.push({
			conversion_event_id: '',
			country_code: countryCode,
			hashed_email: '',
			user_agent: '',
			event_info: {
				...ssAdjustDeviceIdsFromDetail(initialEvent.detail),
				app_token: initialEvent.detail?.app_token || eventConfig.appToken,
				device_id: initialEvent.deviceId || '',
				event: eventConfig.eventToken,
				legacy_track_cookie: initialEvent.legacyTrackCookie || '',
				page_name: pageName,
			},
			event_source: [LOG_EVENT_TYPES.ADJUST],
			event_type: THIRD_PARTY_EVENTS.INITIATE_CHECKOUT,
			event_source_url: pageName,
			user_to_product_uuid: '',
		});
	}

	return transformedEvents;
}
